import React, { useMemo, useState } from 'react'
import { ChevronDown, X, AlertCircle, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import type { SocialProfile } from '@/features/social/types'

interface PostProfileSelectorProps {
  profiles: SocialProfile[]
  selectedProfileIds: string[]
  onSelectionChange: (ids: string[]) => void
  multiProfileEnabled: boolean
  error?: string | null
  isSubmitting: boolean
}

export function PostProfileSelector({
  profiles,
  selectedProfileIds,
  onSelectionChange,
  multiProfileEnabled,
  error,
  isSubmitting
}: PostProfileSelectorProps) {
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false)
  const [profileSearch, setProfileSearch] = useState('')

  const selectedProfilesList = useMemo(
    () => profiles.filter(profile => selectedProfileIds.includes(profile.id)),
    [profiles, selectedProfileIds]
  )

  const filteredProfiles = useMemo(() => {
    const term = profileSearch.trim().toLowerCase()
    if (!term) return profiles
    return profiles.filter(profile =>
      profile.name.toLowerCase().includes(term) ||
      profile.platform?.toLowerCase().includes(term) ||
      profile.franchise_region?.toLowerCase().includes(term)
    )
  }, [profiles, profileSearch])

  const toggleProfileSelection = (profileId: string) => {
    if (selectedProfileIds.includes(profileId)) {
      onSelectionChange(selectedProfileIds.filter(id => id !== profileId))
    } else {
      onSelectionChange([...selectedProfileIds, profileId])
    }
  }

  const handleSingleProfileSelect = (value: string) => {
    onSelectionChange(value ? [value] : [])
  }

  const clearProfileSelections = () => {
    onSelectionChange([])
  }

  if (multiProfileEnabled) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <Label className="text-sm font-medium text-muted-foreground">
              Choose profiles
            </Label>
            <p className="text-xs text-muted-foreground">
              Select one or more destinations across your connected platforms.
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setIsProfileDialogOpen(true)}
          className="w-full justify-between"
          disabled={isSubmitting}
        >
          <span>Select Profiles ({selectedProfileIds.length})</span>
          <ChevronDown className="ml-2 h-4 w-4" />
        </Button>
        
        {selectedProfilesList.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {selectedProfilesList.map(profile => (
              <span
                key={profile.id}
                className="flex items-center gap-2 rounded-full border border-border/60 bg-background px-3 py-1 text-sm shadow-sm"
              >
                <span>{profile.name}</span>
                <button
                  type="button"
                  onClick={() => toggleProfileSelection(profile.id)}
                  className="text-muted-foreground hover:text-destructive transition"
                  aria-label={`Remove ${profile.name}`}
                  disabled={isSubmitting}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No profiles selected yet. Choose at least one before publishing.
          </p>
        )}
        
        {error && (
          <div className="flex items-center gap-1 text-sm text-red-500 mt-1">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}

        <Dialog
          open={isProfileDialogOpen}
          onOpenChange={(open) => {
            setIsProfileDialogOpen(open)
            if (!open) {
              setProfileSearch('')
            }
          }}
        >
          <DialogContent className="max-w-xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>Select Profiles</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={profileSearch}
                  onChange={(e) => setProfileSearch(e.target.value)}
                  placeholder="Search by name, platform, or region"
                  className="pl-9"
                />
              </div>
              <ScrollArea className="h-72 pr-2">
                <div className="space-y-2 pb-2">
                  {filteredProfiles.map(profile => {
                    const checked = selectedProfileIds.includes(profile.id)
                    return (
                      <label
                        key={profile.id}
                        className={cn(
                          'flex items-center gap-3 rounded-lg border px-3 py-2 transition hover:border-primary cursor-pointer',
                          checked && 'border-primary bg-primary/5'
                        )}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => toggleProfileSelection(profile.id)}
                        />
                        <div className="flex flex-col">
                          <span className="text-sm font-semibold">{profile.name}</span>
                          <span className="text-xs text-muted-foreground uppercase tracking-wide">
                            {profile.platform} · {profile.franchise_region}
                          </span>
                        </div>
                      </label>
                    )
                  })}
                  {filteredProfiles.length === 0 && (
                    <p className="text-sm text-muted-foreground px-1">No profiles match your search.</p>
                  )}
                </div>
              </ScrollArea>
              <div className="flex items-center justify-between">
                <Button type="button" variant="ghost" size="sm" onClick={clearProfileSelections}>
                  Clear all
                </Button>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setIsProfileDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="button" onClick={() => setIsProfileDialogOpen(false)}>
                    Apply ({selectedProfileIds.length})
                  </Button>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="profile" className="text-sm font-medium text-muted-foreground">
        Choose where to publish
      </Label>
      <Select
        name="profile_id"
        value={selectedProfileIds[0] || ''}
        onValueChange={handleSingleProfileSelect}
        disabled={isSubmitting}
      >
        <SelectTrigger
          className={cn(
            "h-12 text-base",
            error && "border-red-500 focus:ring-red-500"
          )}
        >
          <SelectValue placeholder="Select a social profile" />
        </SelectTrigger>
        <SelectContent>
          {profiles.map((profile) => (
            <SelectItem key={profile.id} value={profile.id} className="py-3">
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="uppercase text-2xs tracking-wider">
                  {profile.platform}
                </Badge>
                <span className="font-medium">{profile.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && (
        <div className="flex items-center gap-1 text-sm text-red-500 mt-1">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}
    </div>
  )
}

